/**
 * Las etiquetas de un chat se cambian DE A UNA, en la base (20-oct-2026; función: 20261020235300).
 *
 * El founder: «al elegir una o varias etiquetas en Smarty como que se buguea: se selecciona otra cosa o no se
 * selecciona». Cada pantalla guardaba la lista entera en cada clic: dos clics seguidos eran dos guardados en carrera y
 * ganaba el último en llegar aunque fuera el viejo; y el tiempo real del primer guardado pisaba en pantalla lo que se
 * acababa de elegir. Con el conector propio, además, el celular devuelve cada etiqueta como eco.
 *
 * ⭐ LA DIFERENCIA, NO LA LISTA: se manda qué poner y qué quitar (`fn_wa_cambiar_etiquetas`) y la base lo aplica sobre
 *    lo que tiene, con la fila tomada. Dos cambios a la vez se aplican los dos. El trigger lo lleva al celular.
 * ⭐ MIENTRAS SE GUARDA, MANDA LA PANTALLA: con cambios en vuelo, el tiempo real no pisa las etiquetas de ese chat
 *    (`lasEtiquetasEstanEnVuelo`); cuando termina el último, se leen una vez y la pantalla queda igual a la base.
 */
import { supabase } from '@/lib/supabase';
import { llevarLasEtiquetasAlCelular } from './llevarLasEtiquetasAlCelular';

export type CambioDeEtiquetas = { poner?: readonly string[]; quitar?: readonly string[] };

const enVuelo = new Map<string, number>();

/** ¿Hay cambios de etiquetas de este chat todavía guardándose? Mientras tanto, el tiempo real no los pisa. */
export function lasEtiquetasEstanEnVuelo(conversationId: string): boolean {
  return (enVuelo.get(conversationId) ?? 0) > 0;
}

/** Qué poner y qué quitar para pasar de una lista a otra. Puro. */
export function laDiferencia(antes: readonly string[] | null | undefined, despues: readonly string[]): { poner: string[]; quitar: string[] } {
  const a = antes ?? [];
  return { poner: despues.filter((t) => !a.includes(t)), quitar: a.filter((t) => !despues.includes(t)) };
}

/**
 * Pone y quita etiquetas sobre lo que el chat tiene en la base y las lleva al celular. Devuelve si se guardó.
 * `alTerminar` recibe cómo quedaron en la base cuando termina el último cambio en vuelo de ese chat.
 */
export async function cambiarLasEtiquetas(
  conversationId: string, cambio: CambioDeEtiquetas, alTerminar?: (tags: string[]) => void,
): Promise<boolean> {
  const poner = [...(cambio.poner ?? [])];
  const quitar = [...(cambio.quitar ?? [])];
  if (poner.length === 0 && quitar.length === 0) return true;
  enVuelo.set(conversationId, (enVuelo.get(conversationId) ?? 0) + 1);
  let ok = false;
  try {
    const { error } = await supabase.rpc('fn_wa_cambiar_etiquetas', { p_conversacion: conversationId, p_poner: poner, p_quitar: quitar });
    ok = !error;
    if (ok) llevarLasEtiquetasAlCelular(conversationId);
  } finally {
    const quedan = (enVuelo.get(conversationId) ?? 1) - 1;
    if (quedan > 0) enVuelo.set(conversationId, quedan);
    else enVuelo.delete(conversationId);
  }
  if (alTerminar && !lasEtiquetasEstanEnVuelo(conversationId)) {
    const { data } = await supabase.from('wa_conversations').select('tags').eq('id', conversationId).maybeSingle<{ tags: string[] | null }>();
    if (data && !lasEtiquetasEstanEnVuelo(conversationId)) alTerminar(data.tags ?? []);
  }
  return ok;
}

/** Lo elegido de una vez (el modal de etiquetas): se ve ya, y se guarda como la diferencia con lo que había. */
export function elegirLasEtiquetas(
  conversationId: string, antes: readonly string[] | null | undefined, despues: readonly string[], aplicar: (tags: string[]) => void,
): Promise<boolean> {
  aplicar([...despues]);
  return cambiarLasEtiquetas(conversationId, laDiferencia(antes, despues), aplicar);
}
